import type {
  InviteUserRequest,
  Settings,
  Tenant,
  TenantCreateRequest,
  TenantDetails,
  UpdateSettingsRequest,
  UpdateTenantSettingsRequest,
  UpdateTenantUserRequest,
} from "./model"
import { baseApi } from "@/base"
import type { Uuid } from "@/utils"

const api = baseApi
  .enhanceEndpoints({ addTagTypes: ["Tenants", "Settings"] })
  .injectEndpoints({
    endpoints: (build) => ({
      getTenants: build.query<Tenant[], void>({
        query: () => "tenants",
        providesTags: ["Tenants"],
      }),
      getCurrentTenant: build.query<TenantDetails, void>({
        query: () => "tenants/current",
        providesTags: ["Tenants"],
      }),
      updateTenantSettings: build.mutation<
        TenantDetails,
        UpdateTenantSettingsRequest
      >({
        query: (body) => ({
          url: "tenants/current",
          method: "PATCH",
          body,
        }),
        invalidatesTags: ["Tenants"],
      }),
      createTenant: build.mutation<Tenant, TenantCreateRequest>({
        query: (body) => ({
          url: "tenants",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Tenants", "Settings"],
      }),
      inviteUser: build.mutation<Tenant, InviteUserRequest>({
        query: (body) => ({
          url: "users/invite",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Tenants", "Settings"],
      }),
      resendInvite: build.mutation<void, Uuid>({
        query: (id) => ({
          url: `users/${id}/reinvite`,
          method: "POST",
        }),
      }),
      getSettings: build.query<Settings, void>({
        query: () => "settings",
        providesTags: ["Settings"],
      }),
      updateSettings: build.mutation<Settings, UpdateSettingsRequest>({
        query: (body) => ({
          url: "settings",
          method: "PATCH",
          body,
        }),
      }),
      removeUser: build.mutation<Tenant, Uuid>({
        query: (id) => ({
          url: `tenants/users/${id}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Tenants"],
      }),
      updateTenantUser: build.mutation<Tenant, UpdateTenantUserRequest>({
        query: ({ userId, ...body }: UpdateTenantUserRequest) => ({
          url: `tenants/users/${userId}`,
          method: "PATCH",
          body,
        }),
        invalidatesTags: ["Tenants"],
      }),
    }),
  })

export const {
  useGetTenantsQuery,
  useGetCurrentTenantQuery,
  useUpdateTenantSettingsMutation,
  useCreateTenantMutation,
  useInviteUserMutation,
  useResendInviteMutation,
  useGetSettingsQuery,
  useUpdateSettingsMutation,
  useRemoveUserMutation,
  useUpdateTenantUserMutation,
} = api
